import { inject, Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import merge from 'lodash-es/merge';
import extend from 'lodash-es/extend';

import { storage } from '@utils/storage.service';

import browserHelper from '@utils/browser-helper';

import { PaymentResource } from '@app/shared/modules/resources/payment.resource';
import { IFBCompany, IFBCompanyLanguage, IFBPlan } from '@interfaces';
import { IS_MFE_MODE } from '../tokens/is-mfe-mode';

const COMPANY_SERVICE_DATA_INITIAL_STATE = {
  languages: {
    defaultLocale: 'en_US',
    enabledLocales: [],
    localeOverrides: []
  },
  registrationPageBranding: {},
  subscription: {
    plan: {} as IFBPlan
  },
  id: ''
} as IFBCompany;

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private readonly isMfe = inject(IS_MFE_MODE);
  // @NB
  // - We set the initial state in BehaviorSubject's constructor
  // - Nobody outside the Store should have access to the BehaviorSubject, because it has the write rights
  // - Writing to state should be handled by specialized Store methods (ex: addTodo, removeTodo, etc)
  // - Create one BehaviorSubject per store entity, for example if you have TodoGroups
  //   create a new BehaviorSubject for it, as well as the observable$, and getters/setters
  private readonly _data = new BehaviorSubject<IFBCompany>(COMPANY_SERVICE_DATA_INITIAL_STATE);

  // Expose the observable$ part of the _data subject (read only stream)
  public data$ = this._data.asObservable();

  constructor(
    private paymentResource: PaymentResource,
    @Inject('Window') private window: Window
  ) {}

  public get data() {
    return this._data.getValue();
  }

  public get subdomain(): string {
    if (browserHelper.isInWrapper || browserHelper.isLocalhost) {
      return storage.getItem('companySubdomain');
    } else {
      return browserHelper.subdomain();
    }
  }

  public domain(host: string = this.window.location.host): string {
    if (browserHelper.hasSubdomain(host)) {
      const subdomain = host.split('.')[0];
      return host.slice(subdomain.length + 1).split(':')[0];
    } else {
      return host;
    }
  }

  public get genericHost() {
    return `${this.subdomain}.${this.domain(environment.API_BASE)}`;
  }

  public get getBaseUrl() {
    return `https://${this.subdomain}.${this.domain(environment.API_BASE)}`;
  }

  public set(data: object): void {
    this._data.next(merge({}, this.data, data));
  }

  /**
   * Since there is no subdomain initially when opening the website via cname
   * it's needed to call metadata with the current url
   */
  public get x1brdHost() {
    if (this.isMfe && this.data.domainName) {
      const host = this.data.domainName + '.' + this.domain(environment.API_BASE);
      return host;
    } else if (browserHelper.isCname() || !this.subdomain) {
      return window.location.host;
    } else {
      return this.genericHost;
    }
  }

  clean(): void {
    // Clean User object properties leaving reference in place
    Object.keys(this.data).forEach(key => ((this.data as any)[key] = null));
    this._data.next(this.data);
  }

  fetchPaymentPlan() {
    return this.paymentResource.getPlanSubscription(this.data.id).pipe(
      tap(response => {
        const data = { ...this.data, subscription: { ...this.data.subscription, ...response } };
        extend(this.data.subscription, response);
        this._data.next(data);
      })
    );
  }

  setEnabledLocales(languages: string[]) {
    extend(this.data.languages.enabledLocales, languages);
    this._data.next(this.data);
  }

  setLanguages(langMetadata: IFBCompanyLanguage) {
    extend(this.data.languages, langMetadata);
    this._data.next(this.data);
  }

  getCompanyLanguage(): string {
    const { defaultLocale, localeOverrides } = this.data.languages;

    const override = localeOverrides?.find(override => override.originalLocale === defaultLocale);

    return override?.replacingLocale ?? defaultLocale;
  }
}
